import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Star, MessageSquare, ArrowRight } from 'lucide-react';
import { tariffsAPI, heroAPI } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TariffsPage = () => {
  const [tariffs, setTariffs] = useState([]);
  const [heroContent, setHeroContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tariffsRes, heroRes] = await Promise.all([
          tariffsAPI.getAll(),
          heroAPI.get()
        ]);

        setTariffs(tariffsRes.data);
        setHeroContent(heroRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="public-page min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="public-page min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Тарифные планы
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Выберите подходящий пакет сообщений для AI-переводчика. Чем больше пакет, тем дешевле одно сообщение.
            </p>
            <div className="flex justify-center">
              <Badge className="bg-blue-600 text-white px-4 py-2 text-lg">
                Все тарифы включают полный функционал
              </Badge>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tariffs Section */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {tariffs.map((tariff, index) => (
              <motion.div
                key={tariff.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className={`relative h-full ${
                  tariff.is_popular 
                    ? 'bg-gradient-to-b from-blue-900/50 to-gray-800 border-blue-500 scale-105' 
                    : 'bg-gray-800 border-gray-700'
                } hover:scale-105 transition-all duration-300`}>
                  {tariff.is_popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-600 text-white flex items-center shadow-lg">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        Популярный
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-white text-2xl mb-4">{tariff.name}</CardTitle>
                    <div className="space-y-2">
                      <div className="text-4xl font-bold text-blue-400">{tariff.price}₽</div>
                      <div className="text-gray-300">{tariff.messages_count} сообщений</div>
                      <div className="text-sm text-gray-500 bg-gray-900/30 rounded-lg py-1 px-2">
                        {tariff.price_per_message?.toFixed(2)}₽ за сообщение
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="flex flex-col flex-1">
                    <Button 
                      className={`w-full mb-6 ${
                        tariff.is_popular 
                          ? 'bg-blue-600 hover:bg-blue-700' 
                          : 'bg-gray-700 hover:bg-gray-600'
                      } text-white`}
                      onClick={() => window.open(heroContent?.bot_link || "https://t.me/lngaua_bot", "_blank")}
                      data-testid={`buy-tariff-${tariff.id}`}
                    >
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Купить пакет
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    
                    {tariff.features && tariff.features.length > 0 && (
                      <div className="space-y-3 flex-1">
                        <div className="text-sm font-medium text-gray-300 mb-3">
                          Что входит в пакет:
                        </div>
                        {tariff.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-300">
                            <CheckCircle className="h-4 w-4 text-green-400 mr-3 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Почему выбирают наш бот?</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Преимущества AI-переводчика @lngaua_bot
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: "🚀",
                title: "Мгновенный перевод",
                description: "Переводы происходят в реальном времени, без задержек"
              },
              {
                icon: "🎯",
                title: "Высокая точность",
                description: "Используем последние достижения AI для максимально точного перевода"
              },
              {
                icon: "🌍",
                title: "Множество языков",
                description: "Поддержка всех популярных языков мира"
              },
              {
                icon: "💬",
                title: "Работа в чатах",
                description: "Переводит сообщения между участниками групповых чатов"
              },
              {
                icon: "🎤",
                title: "Голосовые сообщения",
                description: "Переводит не только текст, но и голосовые сообщения"
              },
              {
                icon: "💰",
                title: "Выгодные цены",
                description: "Чем больше пакет, тем дешевле каждое сообщение"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="bg-gray-800 border-gray-700 h-full text-center">
                  <CardHeader>
                    <div className="text-4xl mb-4">{feature.icon}</div>
                    <CardTitle className="text-white">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-400">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Начните прямо сейчас</h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Получите 15 бесплатных сообщений и оцените качество AI-переводчика
            </p>
            <Button 
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-4"
              onClick={() => window.open(heroContent?.bot_link || "https://t.me/lngaua_bot", "_blank")}
              data-testid="get-started-cta"
            >
              <MessageSquare className="mr-2 h-5 w-5" />
              Попробовать бесплатно
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TariffsPage;
